import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiEdit, FiTrash2, FiUser, FiLock, FiUnlock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import UserModal from '../components/UserModal';
import { useAuth } from '../contexts/AuthContext';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import ResponsiveDataList from '../components/common/ResponsiveDataList';
import MobileCard from '../components/common/MobileCard';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const Users = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const { data: usersData, isLoading } = useQuery(
    'users',
    () => api.get('/users').then(res => res.data),
    {
      onError: (error) => {
        toast.error('Erreur lors du chargement des utilisateurs');
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/users/${id}`),
    {
      onSuccess: () => {
        toast.success('Utilisateur supprimé avec succès');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  );

  const toggleActiveMutation = useMutation(
    ({ id, active }) => api.put(`/users/${id}`, { active }),
    {
      onSuccess: () => {
        toast.success('Statut modifié avec succès');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la modification');
      }
    }
  );

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    const result = await Swal.fire({
      title: `Supprimer l'utilisateur ${user.username} ?`,
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      deleteMutation.mutate(user.id);
      Swal.fire('Supprimé !', "L'utilisateur a été supprimé.", 'success');
    }
  };

  const handleToggleActive = (user) => {
    toggleActiveMutation.mutate({ id: user.id, active: user.active ? 0 : 1 });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      directeur: 'bg-purple-100 text-purple-800',
      secretaire: 'bg-blue-100 text-blue-800',
      enseignant: 'bg-green-100 text-green-800',
      comptable: 'bg-yellow-100 text-yellow-800',
      surveillant: 'bg-gray-100 text-gray-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Utilisateurs</h1>
        <ConditionalButton
          entity="users"
          permission="create"
          onClick={handleAdd}
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
        >
          <FiPlus />
          <span>Nouvel utilisateur</span>
        </ConditionalButton>
      </div>

      <ResponsiveDataList
        isLoading={isLoading}
        isEmpty={!Array.isArray(usersData?.data) || usersData.data.length === 0}
        emptyMessage="Aucun utilisateur trouvé"
        mobileCards={(Array.isArray(usersData?.data) ? usersData.data : []).map((user) => (
          <MobileCard
            key={user.id}
            title={`${user.nom} ${user.prenom}`}
            subtitle={`@${user.username}`}
            badge={
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getRoleBadgeColor(user.role)}`}>
                {user.role}
              </span>
            }
            fields={[
              { label: 'Email', value: user.email },
              {
                label: 'Statut',
                value: user.active ? 'Actif' : 'Inactif',
              },
            ]}
            actions={
              <>
                <button
                  onClick={() => handleToggleActive(user)}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium ${
                    user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                  disabled={!hasPermission('user.update')}
                >
                  {user.active ? <FiUnlock className="mr-1" size={12} /> : <FiLock className="mr-1" size={12} />}
                  {user.active ? 'Actif' : 'Inactif'}
                </button>
                <ConditionalActions
                  entity="users"
                  onEdit={() => handleEdit(user)}
                  onDelete={user.username !== 'admin' ? () => handleDelete(user) : null}
                  editIcon={FiEdit}
                  deleteIcon={FiTrash2}
                  className="flex space-x-2"
                />
              </>
            }
          />
        ))}
      >
        <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(usersData?.data) && usersData.data.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              ) : (
                (Array.isArray(usersData?.data) ? usersData.data : []).map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <FiUser className="h-5 w-5 text-gray-500" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.nom} {user.prenom}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{user.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                        disabled={!hasPermission('user.update')}
                      >
                        {user.active ? (
                          <>
                            <FiUnlock className="mr-1" />
                            Actif
                          </>
                        ) : (
                          <>
                            <FiLock className="mr-1" />
                            Inactif
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <ConditionalActions
                        entity="users"
                        onEdit={() => handleEdit(user)}
                        onDelete={user.username !== 'admin' ? () => handleDelete(user) : null}
                        editIcon={FiEdit}
                        deleteIcon={FiTrash2}
                        className="flex space-x-2"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
      </ResponsiveDataList>

      {/* User Modal */}
      {showModal && (
        <UserModal
          user={selectedUser}
          roles={usersData?.roles || []}
          onClose={() => {
            setShowModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setSelectedUser(null);
            queryClient.invalidateQueries('users');
          }}
        />
      )}
    </div>
  );
};

export default Users;